import { useState } from 'react'
import StatusAdvance from './StatusAdvance.jsx'
import { readRenderResult, renderSettled, pendingReason } from './buildPayload.js'
import GptBridgePanel from '../gptBridge/GptBridgePanel.jsx'
import { latestBridgeImage, provenanceLine } from '../gptBridge/bridgeModel.js'
import styles from './Studio.module.css'

/**
 * THE RESULT — the multi-view set, the assembled prompt, the build record.
 *
 * `renderPending: true` is a REAL, COMPLETE STATE, not an error: the build
 * record was written, every view's prompt was assembled and the locked layer
 * was asserted intact — only the image call was skipped because OPENAI_API_KEY
 * lives on Railway and not in this environment. So it renders as the prompts +
 * a plain statement of what happens next, never as a failure.
 *
 * A per-view `error` is shown against THAT view and costs nothing else: the
 * other four still render. `hostedUrl` is the OWNED HubSpot File Manager copy
 * and is preferred over the raw model URL wherever it exists.
 */
export default function StudioResult({ result, statuses, onRecord, onGptImage }) {
  const [showPrompt, setShowPrompt] = useState(false)
  const [view, setView] = useState(null)
  if (!result) return null

  // One pure reader decides what this response means (buildPayload.js), so the
  // renderPending / per-view-error handling is asserted by verify-studio.mjs
  // rather than living only inside this component.
  const read = readRenderResult(result)
  const record = result.record || {}
  const images = result.images || []
  const prompts = result.prompts || []
  const active = view || images[0]?.view || prompts[0]?.view
  const shown = images.find((i) => i.view === active) || images[0]
  const shownPrompt = prompts.find((p) => p.view === active) || prompts[0]
  const rendered = images.filter((i) => i.url || i.hostedUrl)
  /* A view drawn in the GPT is not in record.images (the engine owns that
   * list); it is in the record's HISTORY as a [gpt-bridge] note. Reading it
   * from there means a build reopened from history six weeks later still
   * shows the image and says where it came from. */
  const bridged = latestBridgeImage(record.history, active)
  const engineImage = shown && (shown.hostedUrl || shown.url)

  return (
    <section className={`card ${styles.result}`}>
      <header className={styles.resultHead}>
        <div>
          <div className="field-label">Build</div>
          <div className={styles.buildId}>{result.buildId}</div>
        </div>
        <div className={styles.resultMeta}>
          <span className={styles.badge}>{record.mode || 'render'}</span>
          <span className={styles.badge}>{record.settingPreset}</span>
          <span className={styles.badge}>{record.beastId}</span>
        </div>
      </header>

      {read.pending && (
        <div className={styles.pending}>
          {renderSettled(record) ? (
            <>
              {/* ⚠️ THIS READ AS A DEAD END AND IT IS A STEP. The old
                * headline was "No image from the engine", which lands as a
                * failure — and the GPT panel sat below an empty frame, so the
                * route to the picture looked like a consolation prize. Same
                * facts, ordered as what to do next. */}
              <strong>Prompts assembled. Your GPT draws the image — no API spend.</strong>
              <p>
                The build record is written and all {read.promptCount} view prompts are complete, with the locked layer
                asserted intact. The engine did not draw them here: {pendingReason(record) || 'it recorded no reason.'}{' '}
                Send the view below to your GPT and the image comes back owned, on this build.
              </p>
            </>
          ) : (
            <>
              <strong>Prompts assembled. Waiting on the engine…</strong>
              <p>The build record is written and all {read.promptCount} view prompts are complete. Checking back every few seconds.</p>
            </>
          )}
        </div>
      )}

      {!read.pending && !!read.failedViews.length && (
        <p className={styles.warnNote}>
          {read.failedViews.length} of {read.viewCount} views failed to render. The rest are below — a failed view costs
          only itself.
        </p>
      )}

      {!!prompts.length && (
        <div className={styles.viewTabs} role="tablist">
          {prompts.map((p) => {
            const img = images.find((i) => i.view === p.view)
            return (
              <button
                key={p.view}
                role="tab"
                aria-selected={active === p.view}
                className={`${styles.viewTab} ${active === p.view ? styles.viewTabOn : ''}`}
                onClick={() => setView(p.view)}
              >
                {p.label}
                {img?.error && <span className={styles.dotErr} aria-label="failed" />}
                {(img?.hostedUrl || img?.url) && <span className={styles.dotOk} aria-label="rendered" />}
              </button>
            )
          })}
        </div>
      )}

      {/* ⚠️ ORDER IS THE FIX. The bridge used to sit BELOW an empty frame, so
        * the first thing a rep saw after pressing the primary button was a
        * grey box reading "No image for this view yet" — and the way to
        * actually get a picture was further down, looking like a fallback.
        * When this view has no image, the GPT panel comes FIRST and the empty
        * frame follows as the place the image will land. Once there IS an
        * image the order flips back: picture first, panel underneath for the
        * next view. */}
      {(() => {
        const panel = shownPrompt && result.buildId ? (
          <GptBridgePanel
            key={`${result.buildId}-${shownPrompt.view}`}
            buildId={result.buildId}
            view={shownPrompt.view}
            viewLabel={shownPrompt.label}
            prompt={shownPrompt.prompt}
            crestUrl={record.input?.logoC?.url || ''}
            onAttached={(img) => onGptImage?.(img)}
          />
        ) : null
        const stage = (
          <div className={styles.stage}>
            {engineImage ? (
              <img className={styles.render} src={engineImage} alt={shown.label} />
            ) : bridged ? (
              <img className={styles.render} src={bridged.url} alt={`${shown?.label || active} (from the GPT)`} />
            ) : (
              <div className={styles.stageEmpty}>
                {shown?.error
                  ? <span className={styles.warnNote}>{shown.error}</span>
                  : <span>Your GPT image will appear here once you bring it back.</span>}
              </div>
            )}
          </div>
        )
        const provenance = !engineImage && bridged
          ? <p className={styles.note}>{provenanceLine(bridged)}</p>
          : null
        const hasImage = !!(engineImage || bridged)
        return hasImage
          ? <>{stage}{provenance}{panel}</>
          : <>{panel}{stage}</>
      })()}

      {rendered.length > 0 && (
        <p className={styles.note}>
          {rendered.filter((i) => i.hostedUrl).length} of {rendered.length} rendered views are owned in HubSpot File
          Manager. A view with only a model URL was rendered but not hosted — its error says why.
        </p>
      )}

      {shownPrompt && (
        <div className={styles.promptBox}>
          <button type="button" className={styles.linkBtn} onClick={() => setShowPrompt((v) => !v)}>
            {showPrompt ? 'Hide' : 'Show'} the assembled prompt for {shownPrompt.label}
          </button>
          {showPrompt && (
            <>
              <pre className={styles.prompt}>{shownPrompt.prompt}</pre>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => navigator.clipboard?.writeText(shownPrompt.prompt)}
              >
                Copy prompt
              </button>
            </>
          )}
        </div>
      )}

      <StatusAdvance record={record} statuses={statuses} onAdvanced={onRecord} />

      {!!(record.locked || []).length && (
        <p className={styles.note}>
          Locked and asserted present in every prompt: {record.locked.map((l) => `“${l}”`).join(', ')}.
        </p>
      )}
    </section>
  )
}
